import { useApolloClient } from '@apollo/client';
import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import EmptyState from '@aurora/shared-client/components/common/EmptyState/EmptyState';
import type { ChipItem } from '@aurora/shared-client/components/common/TextChip/TextChip';
import TextChip from '@aurora/shared-client/components/common/TextChip/TextChip';
import {
  ToastAlertVariant,
  ToastVariant
} from '@aurora/shared-client/components/common/ToastAlert/enums';
import type ToastProps from '@aurora/shared-client/components/common/ToastAlert/ToastAlertProps';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useToasts from '@aurora/shared-client/components/context/ToastContext/useToasts';
import type { AsyncSearchableSelectFieldSpec } from '@aurora/shared-client/components/form/AsyncSearchableSelectField/AsyncSearchableSelectField';
import type AsyncSearchableSelectFieldEntry from '@aurora/shared-client/components/form/AsyncSearchableSelectField/AsyncSearchableSelectFieldEntry';
import { FormFieldVariant } from '@aurora/shared-client/components/form/enums';
import InputEditForm from '@aurora/shared-client/components/form/InputEditForm/InputEditForm';
import type { FormSpec } from '@aurora/shared-client/components/form/types';
import editMemberRoleMutation from '@aurora/shared-client/components/memberships/EditMemberRole.mutation.graphql';
import useMutationWithTracing from '@aurora/shared-client/components/useMutationWithTracing';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import ModalFormBuilder from '@aurora/shared-client/helpers/modal/ModalFormBuilder/ModalFormBuilder';
import fetchRolesListBySearchTerm from '@aurora/shared-client/helpers/roles/RolesHelper';
import type { Form, UpdateRolesInput } from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  EditMemberRoleMutation,
  EditMemberRoleMutationVariables
} from '@aurora/shared-generated/types/graphql-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import { checkPolicy } from '@aurora/shared-utils/helpers/objects/PolicyResultHelper';
import { getLog } from '@aurora/shared-utils/log';
import React, { useContext, useEffect, useState } from 'react';
import { Modal, useClassNameMapper } from 'react-bootstrap';
import { useUIDSeed } from 'react-uid';
import type { UserRolesQuery, UserRolesQueryVariables } from '../../../types/graphql-types';
import useTranslation from '../../useTranslation';
import formSchema from './ManageUserRole.form.json';
import localStyles from './ManageUserRoleModal.module.pcss';
import userRoleQuery from '@aurora/shared-client/components/users/UserRoles.query.graphql';

const log = getLog(module);

interface Props {
  /**
   * Whether the modal is currently being displayed
   */
  show: boolean;
  /**
   * Callback function when the modal is hidden or cancelled
   *
   * @callback
   */
  onHide?: () => void;
}

interface UpdateUserRolesProps {
  /**
   * Placeholder field for pre-assigned roles
   */
  preAssignedRoles: string;
  /**
   * Placeholder field for add role link
   */
  addRoleLink: string;
  /**
   * The list of roles
   */
  roles: AsyncSearchableSelectFieldEntry<{ id: string; name: string }>[];
}

interface UserRoleChipItem extends ChipItem {
  /**
   * If the role can be assigned or removed by logged-in user
   */
  canAssignOrRemoveRole: boolean;
}

/**
 *  The manage user role modal
 *
 *  @author amit.agarwal
 */
const ManageUserRoleModal: React.FC<React.PropsWithChildren<Props>> = ({ show, onHide }: Props) => {
  const uidSeed = useUIDSeed();
  const cx = useClassNameMapper(localStyles);
  const i18n = useTranslation(EndUserComponent.MANAGE_USER_ROLE_MODAL);
  const client = useApolloClient();
  const { addToast } = useToasts();
  const { formatMessage, loading: textLoading } = i18n;
  const { contextUser, contextNode, community } = useContext(AppContext);
  const [assignedUserRoles, setAssignedUserRoles] = useState<UserRoleChipItem[]>([]);
  const [removedRoles, setRemovedRoles] = useState<UserRoleChipItem[]>([]);
  const [showAddRoleField, setShowAddRoleField] = useState<boolean>(false);

  const [updateMemberRole] = useMutationWithTracing<EditMemberRoleMutation>(
    module,
    editMemberRoleMutation
  );

  const {
    data: userRoleData,
    loading,
    error
  } = useQueryWithTracing<UserRolesQuery, UserRolesQueryVariables>(module, userRoleQuery, {
    variables: {
      id: contextUser.id,
      roleConstraints: {
        nodeId: { eq: community.id }
      }
    }
  });

  useEffect(() => {
    if (!loading) {
      const { edges } = userRoleData.user.roles;
      const roleEdge = edges.map(role => ({
        label: role.node.name,
        value: role.node.name,
        valid: true,
        canAssignOrRemoveRole: checkPolicy(role.node.rolePolicies.canAssignRemoveRole)
      }));
      setAssignedUserRoles(roleEdge);
    }
  }, [loading, userRoleData]);

  if (textLoading || loading) {
    return null;
  } else if (error) {
    log.error('Error fetching data:', error);
  }

  /**
   * Renders toast banner or flyout
   *
   * @param alertVariant toast alert variant.
   * @param title toast title.
   * @param message toast message.
   * @param autohide whether the toast should be hidden automatically.
   */
  function renderToast(
    alertVariant: ToastAlertVariant,
    title: string,
    message: string,
    autohide = true
  ): void {
    const id = uidSeed(title);
    const toastVariant =
      alertVariant === ToastAlertVariant.DANGER ? ToastVariant.BANNER : ToastVariant.FLYOUT;
    const toastProps: ToastProps = {
      id,
      toastVariant,
      alertVariant,
      title,
      autohide,
      message,
      delay: 4000
    };
    addToast(toastProps);
  }

  /**
   * Renders a role value using the TextChip component
   * @param props
   */
  function renderRoleChip(props): React.ReactElement {
    const {
      data,
      selectProps: { onChange, value },
      isFocused
    } = props;

    const roleItem = { ...data, valid: true };
    return (
      <TextChip
        key={`role-chip-${data.label}`}
        item={roleItem}
        hasFocus={isFocused}
        onRemove={role => {
          const updatedValues = value.filter(v => v.value !== role.value);
          onChange(updatedValues, {
            action: 'remove-value'
          });
        }}
      />
    );
  }

  /**
   * Renders the Empty State as custom no options messages for roles field.
   *
   * @returns React Element
   */
  function renderEmptyState(): React.ReactElement {
    return (
      <EmptyState
        title={formatMessage('ManageUserRoleForm.roles.emptyState.title')}
        description={formatMessage('ManageUserRoleForm.roles.emptyState.description')}
      />
    );
  }

  /**
   * Handle the remove role for a user
   *
   * @param removedRole the role to be removed
   */
  function onRemoveRole(removedRole: UserRoleChipItem) {
    setAssignedUserRoles(assignedUserRoles.filter(role => role.label !== removedRole.label));
    setRemovedRoles([...removedRoles, removedRole]);
  }

  /**
   * Callback function for fetching roles that match with the input value.
   * Roles found will be shown as select options of AsyncSearchableSelectField.
   */
  const fetchOptions = (
    inputValue: string,
    cursor: string
  ): Promise<{
    options: AsyncSearchableSelectFieldEntry<{ id: string; name: string }>[];
    hasMore: boolean;
    cursor: string;
  }> => {
    const rolesToExclude = assignedUserRoles
      .filter(role => !removedRoles.includes(role))
      .map(role => role.label);
    return fetchRolesListBySearchTerm(
      client,
      inputValue,
      rolesToExclude,
      'core',
      0,
      cursor,
      true,
      true
    );
  };

  const rolesField: AsyncSearchableSelectFieldSpec<
    'roles',
    UpdateUserRolesProps,
    { id: string; name: string }
  > = {
    name: 'roles',
    fieldVariant: FormFieldVariant.ASYNC_SEARCHABLE_SELECT,
    focus: false,
    isVisible: {
      watchFields: null,
      callback: () => showAddRoleField
    },
    minimumInputLengthForSearch: 2,
    defaultValue: [],
    fetchOptions,
    isMulti: true,
    isClearable: false,
    customNoOptionsMessage: renderEmptyState,
    customMultiValue: renderRoleChip,
    searchOnFocus: true
  };

  /**
   *  Builds the form spec for manage user role form
   */
  function buildFormSpec(): FormSpec<UpdateUserRolesProps> {
    return new ModalFormBuilder<UpdateUserRolesProps>(
      'ManageUserRoleForm',
      i18n,
      {
        schema: formSchema as Form,
        cx
      },
      {
        formClassName: cx('lia-form')
      }
    )
      .addPlaceholderFormField({
        name: 'preAssignedRoles',
        className: cx('lia-pre-assigned-role-list'),
        formGroupSpec: {
          label: false
        },
        Content: (() => {
          return (
            <>
              {assignedUserRoles &&
                assignedUserRoles.map(role => (
                  <TextChip
                    key={`role-chip-${role.label}`}
                    item={role}
                    onRemove={onRemoveRole}
                    canRemove={role.canAssignOrRemoveRole}
                  />
                ))}
            </>
          );
        }) as React.FC<React.PropsWithChildren<unknown>>
      })
      .addPlaceholderFormField({
        name: 'addRoleLink',
        formGroupSpec: {
          label: false
        },
        isVisible: {
          watchFields: null,
          callback: () => !showAddRoleField
        },
        Content: (() => {
          return (
            <Button
              className={cx('lia-add-role-link lia-g-p-0')}
              data-testid={'Button.AddRole'}
              onClick={() => setShowAddRoleField(true)}
              variant={ButtonVariant.NO_VARIANT}
            >
              {formatMessage('addRole')}
            </Button>
          );
        }) as React.FC<React.PropsWithChildren<unknown>>
      })
      .addField(rolesField)
      .addSubmitAction({
        title: formatMessage('save')
      })
      .addCancelAction({
        title: formatMessage('cancel')
      })
      .build();
  }

  /**
   * Handles form submit. If action is "submit", it makes the mutation call for updating user roles,
   * else it hides the modal.
   *
   * @param result the formdata
   * @param action the form action
   * @param event the form event
   */
  async function handleSubmit(
    result: UpdateUserRolesProps,
    action: string,
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    if (action === 'cancel') {
      onHide();
    }

    if (event && event.type === 'submit') {
      const { roles } = result;
      const rolesToAdd =
        roles?.length > 0
          ? roles.map(role => ({
              roleName: role.label,
              nodeId: contextNode.id
            }))
          : [];
      const rolesToRemove =
        removedRoles.length > 0
          ? removedRoles.map(role => ({
              roleName: role.label,
              nodeId: contextNode.id
            }))
          : [];
      const requestData: UpdateRolesInput = {
        rolesToAdd,
        rolesToRemove
      };

      const updateInputVariable: EditMemberRoleMutationVariables = {
        userId: contextUser.id,
        updateInput: requestData
      };

      const { errors } = await updateMemberRole({
        variables: updateInputVariable
      });

      if (errors?.length > 0) {
        log.error('Error while changing email: %O', errors);
        renderToast(
          ToastAlertVariant.DANGER,
          formatMessage('updateUserRole.errorTitle'),
          formatMessage('updateUserRole.errorMessage')
        );
        return;
      }

      client.cache.evict({
        id: `User:${contextUser.id}`
      });

      renderToast(
        ToastAlertVariant.SUCCESS,
        formatMessage('updateUserRole.successTitle'),
        formatMessage('updateUserRole.successMessage')
      );
      onHide();
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="sm" backdrop="static">
      <Modal.Header className={cx('lia-g-pb-0')} closeButton>
        <Modal.Title data-testid="ManageUserRoleModal.Title">
          {formatMessage('modalTitle')}
          <p className={cx('lia-description')}>{formatMessage('modalDescription')}</p>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <InputEditForm formSpec={buildFormSpec()} onSubmit={handleSubmit} />
      </Modal.Body>
    </Modal>
  );
};

export default ManageUserRoleModal;
